import React, { useState, useEffect, useRef } from 'react';
import './PrivacyPolicy.css';

/**
 * Privacy & Policy page for JG Printing.
 * Last updated: 2025
 */

const sections = [
    {
        id: 'information-we-collect',
        title: 'Information We Collect',
        content: `When you place an order or interact with JG Printing through our website, we may collect the following personal information:

• Full name
• Delivery address
• Contact number
• Email address
• Order details and payment information

We collect this information solely for the purpose of processing your orders and communicating with you about your purchases.`
    },
    {
        id: 'how-we-use-your-information',
        title: 'How We Use Your Information',
        content: `The information we collect is used for the following purposes:

• Processing and fulfilling your orders
• Communicating order updates, confirmations, and delivery details
• Responding to your inquiries and customer service requests
• Improving our products and services
• Sending promotional updates, only if you have opted in

We do not sell, trade, or rent your personal information to third parties.`
    },
    {
        id: 'data-sharing',
        title: 'Data Sharing & Disclosure',
        content: `JG Printing does not share your personal information with outside parties except in the following limited circumstances:

• With trusted third-party delivery and logistics partners strictly for order fulfillment
• When required by law, regulation, or legal process
• To protect the rights, property, or safety of JG Printing, our customers, or others

Any third parties we work with are obligated to keep your information confidential and use it only for the purposes we specify.`
    },
    {
        id: 'data-security',
        title: 'Data Security',
        content: `We take reasonable measures to protect your personal information from unauthorized access, disclosure, alteration, or destruction. However, please be aware that no method of transmission over the internet or electronic storage is 100% secure.

We encourage you to take steps to protect your own information, such as keeping your account credentials confidential and notifying us immediately if you suspect any unauthorized use of your data.`
    },
    {
        id: 'cookies',
        title: 'Cookies & Tracking',
        content: `Our website may use cookies and similar tracking technologies to enhance your browsing experience and analyze site traffic. Cookies are small data files stored on your device.

You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, some features of our website may not function properly without cookies.`
    },
    {
        id: 'your-rights',
        title: 'Your Rights',
        content: `Under the Philippine Data Privacy Act of 2012 (Republic Act No. 10173), you have the right to:

• Be informed of how your data is being collected and used
• Access your personal data held by us
• Correct inaccurate or outdated personal data
• Request erasure or blocking of your personal data
• Object to the processing of your personal data
• File a complaint with the National Privacy Commission

To exercise any of these rights, please contact us at jgprinting2024@gmail.com.`
    },
    {
        id: 'data-retention',
        title: 'Data Retention',
        content: `We retain your personal information only for as long as necessary to fulfill the purposes outlined in this policy, or as required by applicable laws and regulations.

Order records may be retained for accounting and legal compliance purposes. When your data is no longer needed, we will securely delete or anonymize it.`
    },
    {
        id: 'changes-to-policy',
        title: 'Changes to This Policy',
        content: `JG Printing reserves the right to update or modify this Privacy Policy at any time. Changes will be posted on this page with an updated effective date. We encourage you to review this policy periodically.

Your continued use of our website or services after any changes constitutes your acceptance of the updated policy.`
    },
    {
        id: 'contact-us',
        title: 'Contact Us',
        content: `If you have any questions, concerns, or requests regarding this Privacy Policy or how we handle your personal data, please reach out to us:

📧 Email: jgprinting2024@gmail.com
📘 Facebook: facebook.com/jgdigitalprinting
📍 Address: #16 Camatchile Street Purok 4 Lower Bicutan Taguig City, Taguig, Philippines

We aim to respond to all inquiries within 3–5 business days.`
    },
];

function PrivacyPolicy() {
    const [activeSection, setActiveSection] = useState(sections[0].id);
    const observerRef = useRef(null);

    useEffect(() => {
        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveSection(entry.target.id);
                    }
                });
            },
            { rootMargin: '-30% 0px -60% 0px' }
        );

        sections.forEach(({ id }) => {
            const el = document.getElementById(id);
            if (el) observerRef.current.observe(el);
        });

        return () => observerRef.current?.disconnect();
    }, []);

    const scrollToSection = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <main className="privacy-page">

            {/* ── Hero ── */}
            <section className="privacy-hero">
                <div className="privacy-hero-inner">
                    <p className="privacy-eyebrow">Legal</p>
                    <h1 className="privacy-title">Privacy &amp; Policy</h1>
                    <p className="privacy-subtitle">
                        JG Printing is committed to protecting your personal information.
                        This policy explains what we collect, how we use it, and your rights
                        under Philippine data privacy law.
                    </p>
                    <p className="privacy-effective">Effective date: January 1, 2025</p>
                </div>
                <div className="privacy-hero-divider" />
            </section>

            {/* ── Body ── */}
            <div className="privacy-body">
                <div className="privacy-container">

                    {/* Sidebar TOC */}
                    <aside className="privacy-toc">
                        <p className="privacy-toc-label">On this page</p>
                        <nav>
                            {sections.map((s) => (
                                <button
                                    key={s.id}
                                    className={`privacy-toc-item ${activeSection === s.id ? 'privacy-toc-item--active' : ''}`}
                                    onClick={() => scrollToSection(s.id)}
                                >
                                    {s.title}
                                </button>
                            ))}
                        </nav>
                    </aside>

                    {/* Content */}
                    <article className="privacy-content">
                        {sections.map((s, i) => (
                            <div key={s.id} id={s.id} className="privacy-section">
                                <div className="privacy-section-header">
                                    <span className="privacy-section-number">
                                        {String(i + 1).padStart(2, '0')}
                                    </span>
                                    <h2 className="privacy-section-title">{s.title}</h2>
                                </div>
                                <div className="privacy-section-body">
                                    {s.content.split('\n').map((line, j) =>
                                        line.trim() === '' ? null : (
                                            <p key={j} className={line.startsWith('•') ? 'privacy-bullet' : ''}>
                                                {line}
                                            </p>
                                        )
                                    )}
                                </div>
                            </div>
                        ))}
                    </article>

                </div>
            </div>

        </main>
    );
}

export default PrivacyPolicy;